import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function StyleSelector() {
  const [selectedStyle, setSelectedStyle] = useState<number | null>(null);

  const styles = [
    {
      id: 1,
      name: '日式極簡風格',
      subtitle: '目前使用',
      description: '純白背景搭配黑灰階，大量留白，清晰的視覺層級，專業可信賴形象',
      features: ['純白背景', '黑灰階配色', '大量留白', '清晰層級'],
      image: 'https://public.readdy.ai/ai/img_res/c03c3cdd455d195209ffb79d0c514aad.jpg',
      colors: ['#FFFFFF', '#000000', '#6B7280', '#F3F4F6']
    },
    {
      id: 2,
      name: '現代奢華風格',
      subtitle: '深色調',
      description: '深藍黑色背景搭配香檳金點綴，大膽的非對稱排版，玻璃擬態效果，強調高端奢華感',
      features: ['深色背景', '金色點綴', '非對稱排版', '玻璃效果'],
      image: 'https://readdy.ai/api/search-image?query=Luxury%20dark%20modern%20website%20design%20with%20deep%20navy%20black%20background%20and%20champagne%20gold%20accents%2C%20asymmetric%20layout%2C%20glassmorphism%20cards%2C%20premium%20high-end%20aesthetic%2C%20sophisticated%20luxury%20brand%20interface&width=800&height=600&seq=style-02-luxury&orientation=landscape',
      colors: ['#0A0E27', '#D4AF37', '#1E293B', '#334155']
    },
    {
      id: 3,
      name: '現代科技風格',
      subtitle: '科技感',
      description: '淺灰背景搭配深灰與藍色調，漸層效果與光影，數據視覺化元素，強調科技與精準',
      features: ['淺灰背景', '藍色調', '漸層光影', '科技元素'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20tech%20website%20design%20with%20light%20grey%20background%20and%20blue%20accents%2C%20gradient%20effects%20and%20lighting%2C%20data%20visualization%20elements%2C%20futuristic%20precision%20technology%20interface%2C%20clean%20professional%20layout&width=800&height=600&seq=style-03-tech&orientation=landscape',
      colors: ['#F1F5F9', '#3B82F6', '#1E40AF', '#64748B']
    },
    {
      id: 4,
      name: '藝廊展示風格',
      subtitle: '藝術感',
      description: '米白色背景搭配深灰文字，大圖片展示，類似藝術品陳列，突出鑑定品項的美感',
      features: ['米白背景', '大圖展示', '藝術陳列', '優雅質感'],
      image: 'https://readdy.ai/api/search-image?query=Art%20gallery%20exhibition%20website%20design%20with%20cream%20beige%20background%2C%20large%20image%20displays%2C%20museum-like%20presentation%2C%20elegant%20typography%2C%20sophisticated%20art%20curation%20aesthetic%2C%20luxury%20item%20showcase&width=800&height=600&seq=style-04-gallery&orientation=landscape',
      colors: ['#FAF8F3', '#2C2C2C', '#8B7355', '#E8E3D8']
    },
    {
      id: 5,
      name: '雜誌編輯風格',
      subtitle: '時尚感',
      description: '白色背景搭配黑色與單一強調色，不規則網格排版，大標題與留白對比，時尚專業感',
      features: ['白色背景', '不規則網格', '大標題', '時尚排版'],
      image: 'https://readdy.ai/api/search-image?query=Fashion%20magazine%20editorial%20website%20design%20with%20white%20background%2C%20irregular%20grid%20layout%2C%20bold%20typography%2C%20dramatic%20whitespace%20contrast%2C%20modern%20editorial%20style%2C%20sophisticated%20fashion%20brand%20aesthetic&width=800&height=600&seq=style-05-magazine&orientation=landscape',
      colors: ['#FFFFFF', '#000000', '#DC2626', '#F5F5F5']
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-16 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-black">選擇您的設計風格</h1>
              <p className="text-sm text-neutral-600 mt-1">預覽不同風格，選擇最適合三鑑所的設計方案</p>
            </div>
            <Link 
              to="/" 
              className="px-6 py-2.5 bg-black text-white rounded-lg hover:bg-neutral-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首頁
            </Link>
          </div>
        </div>
      </header>

      {/* Styles Grid */}
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 py-16">
        <div className="grid lg:grid-cols-2 gap-8">
          {styles.map((style) => (
            <div 
              key={style.id}
              className={`bg-white rounded-2xl overflow-hidden border-2 transition-all cursor-pointer ${
                selectedStyle === style.id 
                  ? 'border-black shadow-xl' 
                  : 'border-neutral-200 hover:border-neutral-400'
              }`}
              onClick={() => setSelectedStyle(style.id)}
            >
              {/* Image Preview */}
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={style.image} 
                  alt={style.name}
                  className="w-full h-full object-cover"
                />
                {style.subtitle === '目前使用' && (
                  <div className="absolute top-4 right-4 px-4 py-2 bg-black text-white text-xs font-medium rounded-full">
                    目前使用
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-black">{style.name}</h3>
                    <span className="text-sm text-neutral-500">{style.subtitle}</span>
                  </div>
                  <p className="text-base text-neutral-600 leading-relaxed">{style.description}</p>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {style.features.map((feature, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Color Palette */}
                <div>
                  <p className="text-xs text-neutral-500 mb-3">配色方案</p>
                  <div className="flex gap-2">
                    {style.colors.map((color, index) => (
                      <div 
                        key={index}
                        className="w-12 h-12 rounded-lg border border-neutral-200"
                        style={{ backgroundColor: color }}
                        title={color}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <button
                  className={`w-full py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                    selectedStyle === style.id
                      ? 'bg-black text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStyle(style.id);
                  }}
                >
                  {selectedStyle === style.id ? '已選擇此風格' : '選擇此風格'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Confirm Section */}
        {selectedStyle && (
          <div className="mt-12 p-8 bg-white rounded-2xl border-2 border-black">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-black mb-2">
                  您選擇了：{styles.find(s => s.id === selectedStyle)?.name}
                </h3>
                <p className="text-sm text-neutral-600">
                  確認後，我將為您重新設計整個首頁，採用此風格的配色與排版
                </p>
              </div>
              <button
                className="px-8 py-3 bg-black text-white rounded-lg hover:bg-neutral-800 transition-colors whitespace-nowrap cursor-pointer"
                onClick={() => {
                  alert(`您選擇了「${styles.find(s => s.id === selectedStyle)?.name}」\n\n請告訴我：「請使用風格 ${selectedStyle} 重新設計首頁」\n\n我會立即為您實現！`);
                }}
              >
                確認並重新設計
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="max-w-[1400px] mx-auto px-8 lg:px-16 pb-16">
        <div className="bg-neutral-100 rounded-2xl p-8">
          <h4 className="text-lg font-bold text-black mb-4">💡 如何選擇適合的風格？</h4>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-neutral-700">
            <div>
              <strong className="block mb-2">考慮品牌定位</strong>
              <p>如果強調專業可信，選擇極簡風格；如果強調高端奢華，選擇深色奢華風格</p>
            </div>
            <div>
              <strong className="block mb-2">考慮目標客群</strong>
              <p>年輕時尚客群適合雜誌風格；成熟高端客群適合藝廊或奢華風格</p>
            </div>
            <div>
              <strong className="block mb-2">考慮服務特色</strong>
              <p>強調科技鑑定選擇科技風格；強調藝術品質選擇藝廊風格</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
